import React from 'react';
import { PiGlobeHemisphereWestLight } from 'react-icons/pi';
import { Text } from 'src/modules/shared/components/atoms/text';
import { CircleDisplay } from 'src/modules/shared/components/atoms/circle-display';
import { ProviderBrandAvatar } from 'src/modules/shared/components/molecules/provider-brand-avatar';
import type { PropertyPublicationActionItem } from '../../types/property-publication-actions.type';
import { PropertyPublicationActions } from './property-publication-actions';
import type { PropertyPublicationStatusDisplay } from '../../utils/property-publication-display.util';
import { PropertyPublicationInfoBadge } from './property-publication-info-badge';

export interface PropertyPublicationListItem {
  actions: PropertyPublicationActionItem[];
  historyItems?: PropertyPublicationListItem[];
  historyCollapseLabel?: string;
  historyExpandLabel?: string;
  isHistoryItem?: boolean;
  key: string;
  providerId?: 'meli' | 'wasi' | 'toppropiedades' | null;
  providerLabel: string;
  operationLabel: string;
  status: PropertyPublicationStatusDisplay;
  summaryLabel: string;
}

interface PropertyPublicationListProps {
  items: PropertyPublicationListItem[];
}

const PropertyPublicationListRow: React.FC<{
  item: PropertyPublicationListItem;
}> = ({ item }) => {
  const [showHistory, setShowHistory] = React.useState(false);
  const hasHistory = (item.historyItems?.length ?? 0) > 0;

  return (
    <div className="bg-white">
      <div
        className={[
          'flex items-start gap-3 transition-all',
          item.isHistoryItem
            ? 'bg-slate-50 px-4 py-2.5 hover:bg-slate-100'
            : 'px-3 py-3 hover:bg-slate-50 md:px-4 md:py-2.5',
        ].join(' ')}
      >
        <div className="shrink-0 self-start pt-0.5">
          {item.providerId ? (
            <ProviderBrandAvatar providerId={item.providerId} size="sm" />
          ) : (
            <CircleDisplay
              icon={<PiGlobeHemisphereWestLight className="h-4 w-4" />}
              size="sm"
              variant="neutral"
            />
          )}
        </div>

        <div className="min-w-0 flex-1 space-y-1.5 md:space-y-1">
          <div className="flex min-w-0 items-start justify-between gap-2">
            <Text
              size="small"
              weight="medium"
              className="min-w-0 truncate text-slate-900"
            >
              {item.providerLabel}
            </Text>

            <PropertyPublicationActions actions={item.actions} />
          </div>

          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <Text size="small" className="truncate text-slate-600">
              {item.operationLabel}
            </Text>
            <PropertyPublicationInfoBadge display={item.status} />
          </div>

          <div className="flex min-w-0 items-start gap-2">
            <Text
              size="tiny"
              className="block overflow-hidden text-slate-500 line-clamp-2"
            >
              {item.summaryLabel}
            </Text>
          </div>
        </div>
      </div>

      {hasHistory ? (
        <div className="border-t border-slate-100 bg-slate-50/60">
          <button
            type="button"
            className="flex w-full items-center justify-between px-6 py-2 text-left transition-colors hover:bg-slate-100/70"
            onClick={() => setShowHistory((current) => !current)}
          >
            <Text size="small" weight="regular" className="text-secondary-600">
              {showHistory
                ? item.historyCollapseLabel
                : item.historyExpandLabel}
            </Text>
            <Text size="tiny" className="text-secondary-600">
              {showHistory ? '−' : '+'}
            </Text>
          </button>

          {showHistory ? (
            <div className="divide-y divide-slate-200 border-t border-slate-100">
              {item.historyItems?.map((historyItem) => (
                <PropertyPublicationListRow
                  key={historyItem.key}
                  item={historyItem}
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

/**
 * Compact operation/provider publication matrix for the property editor rail.
 */
export const PropertyPublicationList: React.FC<
  PropertyPublicationListProps
> = ({ items }) => {
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="divide-y divide-slate-200">
        {items.map((item) => (
          <PropertyPublicationListRow key={item.key} item={item} />
        ))}
      </div>
    </div>
  );
};
